import mixpanel from "mixpanel-browser";

let loaded = false;
let identified = false;

/**
 * Safely serialize an object to prevent circular reference errors in Mixpanel.
 * Removes circular references, functions, and deeply nested objects.
 * @param {object} obj - The object to serialize
 * @param {number} maxDepth - Maximum nesting depth (default: 3)
 * @returns {object} - Safe, serializable object
 */
const safeSerialize = (obj, maxDepth = 3) => {
  if (!obj || typeof obj !== 'object') return obj;

  const seen = new WeakSet();

  const serialize = (value, depth) => {
    // Handle primitives
    if (value === null || value === undefined) return value;
    if (typeof value !== 'object') return value;

    // Skip functions, symbols, etc.
    if (typeof value === 'function') return undefined;

    // Prevent circular references
    if (seen.has(value)) return '[Circular]';

    // Limit depth to prevent stack overflow on deeply nested objects
    if (depth > maxDepth) return '[Object]';

    seen.add(value);

    // Handle arrays
    if (Array.isArray(value)) {
      return value.slice(0, 10).map(item => serialize(item, depth + 1));
    }

    // Handle Date objects
    if (value instanceof Date) {
      return value.toISOString();
    }

    // Handle Firestore Timestamps
    if (value.toDate && typeof value.toDate === 'function') {
      return value.toDate().toISOString();
    }

    // Handle regular objects
    const result = {};
    const keys = Object.keys(value).slice(0, 50); // Limit number of keys
    for (const key of keys) {
      try {
        const serialized = serialize(value[key], depth + 1);
        if (serialized !== undefined) {
          result[key] = serialized;
        }
      } catch (e) {
        // Skip properties that throw errors when accessed
        result[key] = '[Error]';
      }
    }

    return result;
  };

  return serialize(obj, 0);
};

// 1) init once
export function initAnalytics() {
  if (loaded) return;
  mixpanel.init("a8560c911edbbd33c1a4258b740039fb", {
    track_pageview: false, // we’ll send our own page views
  });
  loaded = true;
}

// 2) track (Anonymous or Identified)
export const track = (event, props) => {
  if (!loaded) return;
  try {
    mixpanel.track(event, safeSerialize(props));
  } catch (e) {
    console.warn('Mixpanel track error:', e.message);
  }
};

// 3) identify with a known userId (call after Kundli or when LS id exists)
export const identify = (userId) => {
  if (!loaded || !userId) return;
  mixpanel.identify(String(userId));
  identified = true;
};

// 3b) identify directly from localStorage key (for returning users)
export const identifyFromLocalStorage = (LS_KEY) => {
  if (!loaded) return null;
  const id = localStorage.getItem(LS_KEY);
  if (!id) return null;
  identify(id);
  return id;
};

// 4) optional: clear identity (call on logout)
export const resetAnalytics = () => {
  if (!loaded) return;
  mixpanel.reset();
  identified = false;
};

// 5) optional: alias ONCE right after signup (skip if you don’t care)
export const aliasOnce = (userId) => {
  if (!loaded || !userId) return;
  mixpanel.alias(String(userId));
};

// 6) Set people properties
export const setPeople = (props) => {
  if (!loaded) return;
  try {
    mixpanel.people.set(safeSerialize(props));
  } catch (e) {
    console.warn('Mixpanel setPeople error:', e.message);
  }
};

// 7) Merge two distinct_ids into one (call when duplicate detected)
// This links a new user ID to an existing canonical ID, consolidating event history
export const mergeIdentities = (canonicalId, aliasId) => {
  if (!loaded || !canonicalId || !aliasId || canonicalId === aliasId) return;

  try {
    // Use Mixpanel's $merge API to combine profiles
    mixpanel.track('$merge', {
      $distinct_ids: [String(canonicalId), String(aliasId)]
    });

    // Re-identify as the canonical ID going forward
    mixpanel.identify(String(canonicalId));
    console.log(`[MIXPANEL] Merged ${aliasId} into ${canonicalId}`);
  } catch (e) {
    console.warn('Mixpanel merge error:', e.message);
  }
};
